import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { getUroSocket } from '../../../services/URO';
import { NANO_BANANA_PRO_MODEL_ID } from '../../../components/JobDetailView/utils';
import { useGalleryPagination } from './hooks/useGalleryPagination';
import AppScreenWrapper from '../../../components/app-shell/AppScreenWrapper';
import Sidebar from '../../../components/app-shell/Sidebar';
import Topbar from '../../../components/app-shell/Topbar';
import ScreenOrbs from '../../../components/app-shell/ScreenOrbs';
import useClientData from '../../../hooks/useClientData';
import useResponsive from '../../../utils/useResponsive';
import GenerationCard, { normalizeGalleryJob } from '../../../components/GenerationCard';
import ZenGenerationModal from '../zenScreen/components/ZenGenerationModal';

/* ─── searchable dropdown ─────────────────────────────────────────────────── */

function SearchableSelect({ options, value, onChange, placeholder = 'All' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  const selected = options.find(o => o.value === value);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => { setOpen(p => !p); setSearch(''); }}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          height: 30, padding: '0 10px 0 12px', borderRadius: 8, cursor: 'pointer',
          background: value ? 'rgba(255,77,141,.08)' : 'rgba(255,255,255,.04)',
          border: `1px solid ${value ? 'rgba(255,77,141,.30)' : 'var(--border)'}`,
          color: value ? '#FFC2B5' : 'var(--text-3)', fontSize: 12, fontWeight: 500,
          minWidth: 130, maxWidth: 240,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}>
          {selected ? selected.label : placeholder}
        </span>
        {value && (
          <span
            onClick={e => { e.stopPropagation(); onChange(null); setOpen(false); }}
            style={{ color: 'rgba(255,194,181,.6)', fontSize: 16, lineHeight: 1, flexShrink: 0, cursor: 'pointer' }}
          >×</span>
        )}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0, opacity: .6 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 600,
          width: 264, background: 'rgba(10,10,12,.98)', border: '1px solid rgba(255,255,255,.12)',
          borderRadius: 10, boxShadow: '0 16px 48px rgba(0,0,0,.75)', overflow: 'hidden',
        }}>
          <div style={{ padding: '8px 8px 4px' }}>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search…"
              style={{
                width: '100%', height: 30, background: 'rgba(255,255,255,.06)',
                border: '1px solid rgba(255,255,255,.1)', borderRadius: 7,
                padding: '0 10px', color: '#fff', fontSize: 12, outline: 'none',
                boxSizing: 'border-box', fontFamily: 'inherit',
              }}
            />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            <button onClick={() => { onChange(null); setOpen(false); }} style={optBtnStyle(!value)}>
              {placeholder}
            </button>
            {filtered.map(o => (
              <button key={o.value} onClick={() => { onChange(o.value); setOpen(false); }} style={optBtnStyle(value === o.value)}>
                {o.label}
              </button>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: '12px', fontSize: 12, color: 'var(--text-4)', textAlign: 'center' }}>No results</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function optBtnStyle(active) {
  return {
    width: '100%', textAlign: 'left', padding: '8px 12px',
    background: active ? 'rgba(255,77,141,.08)' : 'transparent',
    border: 'none', color: active ? '#FFC2B5' : 'var(--text-2)',
    fontSize: 12, cursor: 'pointer', display: 'block',
  };
}

/* ─── main ─────────────────────────────────────────────────────────────────── */

export default function GalleryScreen() {
  const navigate = useNavigate();
  const { balance, clientName, clientEmail } = useClientData();
  const { isMobile, isTablet } = useResponsive();

  const [activeFilter, setActiveFilter] = useState('');   // '' | 'image' | 'workflow'
  const [modelFilter, setModelFilter] = useState(null);   // model_id string | null
  const [detailViewJob, setDetailViewJob] = useState(null);

  const { jobs, setJobs, isLoading, isLoadingMore, hasMore, fetchInitial, fetchMore, reset } =
    useGalleryPagination({ job_type: activeFilter, model_id: modelFilter });

  // Reset + refetch when either filter changes
  useEffect(() => { reset(); fetchInitial(); }, [activeFilter, modelFilter]); // eslint-disable-line

  /* socket live updates */
  useEffect(() => {
    const socket = getUroSocket();
    const updateJob = (d, isWorkflow = false) => {
      const id = isWorkflow ? d.workflow_job_id : d.image_job_id;
      if (!id) return;
      setJobs(prev => {
        const idx = prev.findIndex(j => j.image_job_id === id || j.workflow_job_id === id);
        const norm = { ...d, job_id: id, job_type: isWorkflow ? 'workflow' : 'image' };
        if (idx >= 0) { const u = [...prev]; u[idx] = { ...prev[idx], ...norm }; return u; }
        return [norm, ...prev];
      });
    };
    const h = {
      'job:created':         d => updateJob({ ...d, status: 'pending' }),
      'job:processing':      d => updateJob({ ...d, status: 'processing' }),
      'job:complete':        d => updateJob({ ...d, status: 'completed', results: d.results }),
      'job:error':           d => updateJob({ ...d, status: 'failed' }),
      'workflow:created':    d => updateJob({ ...d, status: 'pending' }, true),
      'workflow:processing': d => updateJob({ ...d, status: 'processing' }, true),
      'workflow:complete':   d => updateJob({ ...d, status: 'completed', results: d.results }, true),
      'workflow:failed':     d => updateJob({ ...d, status: 'failed' }, true),
    };
    Object.entries(h).forEach(([e, fn]) => socket.on(e, fn));
    return () => Object.entries(h).forEach(([e, fn]) => socket?.off?.(e, fn));
  }, [setJobs]);

  /* infinite scroll */
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMore && !isLoadingMore) fetchMore();
    }, { threshold: 0.1 });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [hasMore, isLoadingMore, fetchMore]);

  /* normalize all jobs once, used for grid + modal generations list */
  const normalizedJobs = useMemo(() => jobs.map(normalizeGalleryJob), [jobs]);

  /* unique model options derived from loaded jobs */
  const modelOptions = useMemo(() => {
    const seen = new Map();
    jobs.forEach(j => {
      const isWorkflow = j.job_type === 'workflow';
      const id = isWorkflow ? j.workflow_definition_id : j.model_id;
      const name = isWorkflow
        ? (j.workflow_definition_name || 'Workflow')
        : (j.model_name || 'Model');
      if (id && !seen.has(id)) seen.set(id, { value: id, label: isWorkflow ? `⚙ ${name}` : name });
    });
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [jobs]);

  /* masonry: 4-col desktop, 3 tablet, 2 mobile — count drives both the JS
     split and the grid template */
  const colCount = isMobile ? 2 : isTablet ? 3 : 4;
  const cols = Array.from({ length: colCount }, () => []);
  normalizedJobs.forEach((j, i) => cols[i % colCount].push({ gen: j, idx: i }));

  const handleReuse = useCallback((gen) => {
    const workflowId = gen.model?.workflow_definition_id;
    const modelId = gen.model?.model_id;
    navigate(
      workflowId ? `/zen/workflow/${workflowId}` : `/zen/model/${modelId}`,
      { state: { reuseJob: true, inputParams: gen.input_params, inputImages: gen.input_images, inputTexts: gen.input_texts } }
    );
  }, [navigate]);

  const handleEditImage = useCallback((url) => {
    navigate(`/zen/model/${NANO_BANANA_PRO_MODEL_ID}?image_url=${encodeURIComponent(url)}`);
  }, [navigate]);

  const formatCost = (v) => {
    if (v == null || Number.isNaN(Number(v))) return '~';
    const n = Number(v);
    if (n >= 1) return n.toFixed(2);
    if (n >= 0.01) return n.toFixed(4);
    return n.toPrecision(2);
  };

  return (
    <AppScreenWrapper>
      <div className="screen-root" style={{ display: 'flex' }}>
        <Sidebar balance={balance} name={clientName} email={clientEmail} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
          <ScreenOrbs tone="pink" />
          <Topbar title="Gallery" name={clientName} />

          <div className="scroll-y app-screen-content" style={{ flex: 1, padding: '24px 32px 40px', position: 'relative', zIndex: 1 }}>

            {/* ── Filter bar ── */}
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                {/* Type tabs — All / Models / Workflows */}
                {[
                  { l: 'All',       v: '' },
                  { l: 'Models',    v: 'image' },
                  { l: 'Workflows', v: 'workflow' },
                ].map(f => (
                  <button key={f.v} onClick={() => { setActiveFilter(f.v); setModelFilter(null); }} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    height: 30, padding: '0 14px', borderRadius: 999, cursor: 'pointer',
                    background: activeFilter === f.v ? 'rgba(255,77,141,.10)' : 'rgba(255,255,255,.04)',
                    border: `1px solid ${activeFilter === f.v ? 'rgba(255,77,141,.35)' : 'var(--border)'}`,
                    color: activeFilter === f.v ? '#FFC2B5' : 'var(--text-2)', fontSize: 13, fontWeight: 600,
                  }}>
                    {f.l}
                  </button>
                ))}

                {/* Separator */}
                <div style={{ width: 1, background: 'var(--border)', height: 20, margin: '0 2px', alignSelf: 'center' }} />

                {/* Searchable model/workflow dropdown */}
                <SearchableSelect
                  options={modelOptions}
                  value={modelFilter}
                  onChange={setModelFilter}
                  placeholder={
                    activeFilter === 'workflow' ? 'All Workflows'
                    : activeFilter === 'image'  ? 'All Models'
                    : 'All Models & Workflows'
                  }
                />

              </div>

              <div className="row" style={{ gap: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{jobs.length.toLocaleString()} results</span>
              </div>
            </div>

            {/* ── Grid ── */}
            {isLoading ? (
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${colCount}, minmax(0,1fr))`, gap: 14 }}>
                {Array.from({ length: isMobile ? 6 : 12 }).map((_, i) => (
                  <div key={i} className="card" style={{ aspectRatio: '4/3' }} />
                ))}
              </div>
            ) : jobs.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 0', gap: 12 }}>
                <div style={{ fontSize: 48 }}>📭</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-2)' }}>No generations found</div>
                <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
                  {(activeFilter || modelFilter) ? 'Try a different filter' : 'Create something in Zen to see it here'}
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${colCount}, minmax(0,1fr))`, gap: 14 }}>
                  {cols.map((col, ci) => (
                    <div key={ci} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {col.map(({ gen }) => (
                        <GenerationCard
                          key={gen.id}
                          generation={gen}
                          isActive={detailViewJob?.id === gen.id}
                          onViewDetails={() => setDetailViewJob(gen)}
                          onRerun={() => handleReuse(gen)}
                          onEditImage={handleEditImage}
                          formatCost={formatCost}
                        />
                      ))}
                    </div>
                  ))}
                </div>

                <div ref={sentinelRef} style={{ height: 1 }} />
                {isLoadingMore && <div style={{ textAlign: 'center', padding: '16px 0', fontSize: 13, color: 'var(--text-3)' }}>Loading more…</div>}
                {!hasMore && jobs.length > 0 && <div style={{ textAlign: 'center', padding: '16px 0', fontSize: 12, color: 'var(--text-4)' }}>All {jobs.length} generations loaded</div>}
              </>
            )}

          </div>
        </div>
      </div>

      {/* ── Generation detail modal — same as Zen ── */}
      {detailViewJob && (
        <ZenGenerationModal
          generation={detailViewJob}
          generations={normalizedJobs}
          onClose={() => setDetailViewJob(null)}
          onReuse={(gen) => { handleReuse(gen); setDetailViewJob(null); }}
          onIterateWith={(url) => { handleEditImage(url); setDetailViewJob(null); }}
          onEditImage={(url) => { handleEditImage(url); setDetailViewJob(null); }}
          onUseAsInput={(url) => { handleEditImage(url); setDetailViewJob(null); }}
          formatCost={formatCost}
        />
      )}
    </AppScreenWrapper>
  );
}
